import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageShell } from '@/components/Layout';
import { Device } from '@/lib/device';
import { useToast } from '@/lib/ui';
import { useConfirmedMutation } from '@/api/mutations';
import {
  fetchAnnotations, createAnnotationFn, updateAnnotationFn, deleteAnnotationFn,
} from '@/api/endpoints';
import type { Annotation, AnnotationListQuery, AnnotationWriteInput } from '@/api/types';

const PAGE_SIZE = 50;

// time / timeend are LOCAL wall-clock strings ("YYYY-MM-DDTHH:MM:SS").
// Format WITHOUT new Date() — a Date parse would re-apply the browser tz.
function fmtLocal(s: string): string {
  const [d, t = ''] = s.split('T');
  const [y, mo, da] = d.split('-');
  if (!y || !mo || !da) return s;
  return `${da}/${mo}/${y} ${t.slice(0, 5)}`;
}
// datetime-local <input> (with step=1) wants "YYYY-MM-DDTHH:MM:SS" — keep the
// seconds so editing an externally-created row does not silently truncate them.
const toInput = (s: string): string => s.slice(0, 19);

export default function Annotations({ device }: { device: Device }) {
  const toast = useToast();
  const qc = useQueryClient();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  // null = closed · 'new' = create · Annotation = edit
  const [editing, setEditing] = useState<Annotation | 'new' | null>(null);

  const query: AnnotationListQuery = {
    q: search.trim() || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    order: 'asc',
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['annotations', query],
    queryFn: () => fetchAnnotations(query),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // After a delete empties a non-first page (offset past the end), walk back so
  // the user is never stranded on an out-of-range empty page.
  useEffect(() => {
    if (!isLoading && rows.length === 0 && page > 0) setPage((p) => Math.max(0, p - 1));
  }, [isLoading, rows.length, page]);

  const refresh = () => { qc.invalidateQueries({ queryKey: ['annotations'] }); };

  const saveMut = useConfirmedMutation(
    (v: { id: number | null; input: AnnotationWriteInput }) =>
      v.id == null ? createAnnotationFn(v.input) : updateAnnotationFn(v.id, v.input),
    {
      title: 'Enregistrer l’annotation',
      message: 'Confirmer l’écriture en base ?',
      onSuccess: () => { toast.push('Annotation enregistrée', 'success'); setEditing(null); refresh(); },
      onError: (e) => { if (e.message !== 'ConfirmRejected') toast.push('Échec de l’enregistrement', 'error'); },
    },
  );
  const deleteMut = useConfirmedMutation(
    (id: number) => deleteAnnotationFn(id),
    {
      title: 'Supprimer l’annotation',
      message: 'L’annotation sera supprimée définitivement. Action irréversible.',
      destructive: true,
      onSuccess: () => { toast.push('Annotation supprimée', 'success'); setEditing(null); refresh(); },
      onError: (e) => { if (e.message !== 'ConfirmRejected') toast.push('Échec de la suppression', 'error'); },
    },
  );

  const busy = saveMut.isPending || deleteMut.isPending;

  return (
    <PageShell device={device} title="Annotations" breadcrumb="Services">
      <div className="p-4 md:p-6 space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="search"
            placeholder="Rechercher évènement / tags…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="h-9 px-3 rounded-md border border-input bg-background text-sm flex-1 min-w-[180px] max-w-xs"
          />
          <span className="text-xs text-muted-foreground ml-auto">
            {isLoading ? 'Chargement…' : `${total} annotation(s)`}
          </span>
          <button
            onClick={() => setEditing('new')}
            disabled={busy}
            className="h-9 px-3 rounded-md text-sm font-medium bg-primary text-primary-foreground disabled:opacity-40"
          >
            + Nouvelle annotation
          </button>
        </div>

        {isError ? (
          <div className="p-8 text-center">
            <p className="text-destructive font-medium">Erreur de chargement</p>
            <p className="text-sm text-muted-foreground mt-1">{error instanceof Error ? error.message : 'Erreur inconnue'}</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5 w-14">id</th>
                  <th className="text-left px-4 py-2.5 whitespace-nowrap">Date Debut local</th>
                  <th className="text-left px-4 py-2.5 whitespace-nowrap">Date Fin local</th>
                  <th className="text-left px-4 py-2.5">Evennement</th>
                  <th className="text-left px-4 py-2.5">tags</th>
                  <th className="w-28 px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => (
                  <tr key={r.id} className="cursor-pointer hover:bg-accent/30 select-none" onClick={() => setEditing(r)} title="Cliquer pour modifier">
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{r.id}</td>
                    <td className="px-4 py-2.5 text-xs font-mono whitespace-nowrap">{fmtLocal(r.time)}</td>
                    <td className="px-4 py-2.5 text-xs font-mono whitespace-nowrap">{fmtLocal(r.timeend)}</td>
                    <td className="px-4 py-2.5 truncate max-w-md">{r.text}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground truncate max-w-[200px]">{r.tags}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button
                        className="text-xs text-primary hover:underline disabled:opacity-40"
                        disabled={busy}
                        onClick={(e) => { e.stopPropagation(); setEditing(r); }}
                      >
                        Modifier
                      </button>
                      <button
                        className="text-xs text-destructive hover:underline disabled:opacity-40 ml-3"
                        disabled={busy}
                        onClick={(e) => { e.stopPropagation(); deleteMut.mutate(r.id); }}
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
                {!isLoading && rows.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">Aucune annotation.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-end gap-2 text-sm">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="h-8 px-3 rounded-md border border-border disabled:opacity-40">←</button>
            <span className="text-xs text-muted-foreground">page {page + 1} / {pages}</span>
            <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} className="h-8 px-3 rounded-md border border-border disabled:opacity-40">→</button>
          </div>
        )}
      </div>

      {editing && (
        <AnnotationFormSheet
          annotation={editing === 'new' ? null : editing}
          busy={busy}
          onClose={() => setEditing(null)}
          onSave={(input) => saveMut.mutate({ id: editing === 'new' ? null : editing.id, input })}
          onDelete={editing === 'new' ? undefined : () => deleteMut.mutate(editing.id)}
        />
      )}
    </PageShell>
  );
}

function AnnotationFormSheet({
  annotation, busy, onClose, onSave, onDelete,
}: {
  annotation: Annotation | null;
  busy: boolean;
  onClose: () => void;
  onSave: (input: AnnotationWriteInput) => void;
  onDelete?: () => void;
}) {
  const [time, setTime] = useState(annotation ? toInput(annotation.time) : '');
  const [timeend, setTimeend] = useState(annotation ? toInput(annotation.timeend) : '');
  const [text, setText] = useState(annotation?.text ?? '');
  const [tags, setTags] = useState(annotation?.tags ?? '');

  const valid = time !== '' && timeend !== '' && text.trim() !== '' && tags.trim() !== '';
  const endBeforeStart = time !== '' && timeend !== '' && timeend < time;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || endBeforeStart) return;
    // Send values verbatim — trimming only gates validity (above), so editing one
    // field never silently rewrites another field's intentional whitespace.
    onSave({ time, timeend, text, tags });
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full md:w-[560px] bg-card border-l border-border shadow-2xl overflow-y-auto flex flex-col">
        <div className="sticky top-0 bg-background/95 backdrop-blur border-b border-border px-5 py-3 flex items-start justify-between z-10">
          <div>
            <div className="text-xs text-muted-foreground">
              {annotation ? <>Annotation <span className="font-mono">#{annotation.id}</span></> : 'Nouvelle annotation'}
            </div>
            <h2 className="text-lg font-bold mt-0.5">{annotation ? 'Modifier' : 'Créer'}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-muted" aria-label="Fermer">✕</button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4 flex-1">
          <label className="block text-sm">
            <span className="text-muted-foreground text-xs uppercase font-medium">Date Debut local</span>
            <input
              type="datetime-local" step={1} required value={time} onChange={(e) => setTime(e.target.value)}
              className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="text-muted-foreground text-xs uppercase font-medium">Date Fin local</span>
            <input
              type="datetime-local" step={1} required value={timeend} onChange={(e) => setTimeend(e.target.value)}
              className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            />
          </label>
          {endBeforeStart && (
            <p className="text-xs text-destructive">La date de fin est antérieure à la date de début.</p>
          )}
          <label className="block text-sm">
            <span className="text-muted-foreground text-xs uppercase font-medium">Evennement</span>
            <input
              type="text" required maxLength={100} value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Ex. Cyclone Belal"
              className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            />
            <span className="text-[10px] text-muted-foreground">{text.length}/100</span>
          </label>
          <label className="block text-sm">
            <span className="text-muted-foreground text-xs uppercase font-medium">tags</span>
            <input
              type="text" required maxLength={100} value={tags} onChange={(e) => setTags(e.target.value)}
              placeholder="Ex. meteo,alerte"
              className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            />
            <span className="text-[10px] text-muted-foreground">{tags.length}/100 · champ texte libre</span>
          </label>
        </form>

        <div className="sticky bottom-0 bg-background/95 backdrop-blur border-t border-border px-5 py-3 flex gap-2 justify-between">
          <div className="flex gap-2">
            <button onClick={onClose} className="h-9 px-4 rounded-md bg-secondary text-secondary-foreground text-sm font-medium">Annuler</button>
            {onDelete && (
              <button onClick={onDelete} disabled={busy} className="h-9 px-4 rounded-md border border-destructive text-destructive text-sm font-medium disabled:opacity-40">Supprimer</button>
            )}
          </div>
          <button
            onClick={submit}
            disabled={busy || !valid || endBeforeStart}
            className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-40"
          >
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </aside>
    </div>
  );
}
